'use client';

import { MockDataMarker } from '@/lib/utils/mockDataMarker';
import { cssVars } from '@/styles/theme';
import { ChevronDown, ChevronUp, Filter, X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';

interface FilterOption {
  id: string;
  label: string;
  count?: number;
}

interface ServiceFiltersProps {
  categories?: FilterOption[];
  onCategoryChange?: (categoryIds: string[]) => void;
  onPriceRangeChange?: (min: number, max: number) => void;
  onDeliveryTimeChange?: (maxDays: number) => void;
  onRatingChange?: (minRating: number) => void;
  isMockData?: boolean;
}

export default function ServiceFilters({
  categories = [],
  onCategoryChange,
  onPriceRangeChange,
  onDeliveryTimeChange,
  onRatingChange,
  isMockData = false,
}: ServiceFiltersProps) {
  const locale = useLocale();
  const t = useTranslations('pages.filters');
  const isRTL = locale === 'ar';

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState<number>(0);
  const [priceMax, setPriceMax] = useState<number>(10000);
  const [deliveryTime, setDeliveryTime] = useState<number>(30);
  const [minRating, setMinRating] = useState<number>(0);

  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    price: true,
    delivery: true,
    rating: true,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleCategoryToggle = (categoryId: string) => {
    const newSelected = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];

    setSelectedCategories(newSelected);
    onCategoryChange?.(newSelected);
  };

  const handlePriceChange = () => {
    onPriceRangeChange?.(priceMin, priceMax);
  };

  const handleDeliveryTimeChange = (days: number) => {
    setDeliveryTime(days);
    onDeliveryTimeChange?.(days);
  };

  const handleRatingChange = (rating: number) => {
    setMinRating(rating);
    onRatingChange?.(rating);
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setPriceMin(0);
    setPriceMax(10000);
    setDeliveryTime(30);
    setMinRating(0);
    onCategoryChange?.([]);
    onPriceRangeChange?.(0, 10000);
    onDeliveryTimeChange?.(30);
    onRatingChange?.(0);
  };

  const hasActiveFilters =
    selectedCategories.length > 0 ||
    priceMin > 0 ||
    priceMax < 10000 ||
    deliveryTime < 30 ||
    minRating > 0;

  return (
    <div
      className="rounded-3xl border-2 p-6"
      style={{
        backgroundColor: cssVars.neutral.surface,
        borderColor: cssVars.neutral.border,
      }}
    >
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5" style={{ color: cssVars.primary.DEFAULT }} />
          <h3 className="text-lg font-bold" style={{ color: cssVars.secondary.DEFAULT }}>
            {t('filters')}
          </h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1 text-sm font-semibold transition-opacity hover:opacity-70"
            style={{ color: cssVars.accent.warm }}
          >
            <X className="h-4 w-4" />
            {t('clearAll')}
          </button>
        )}
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('categories')}
            className="mb-3 flex w-full items-center justify-between"
          >
            <span className="font-semibold" style={{ color: cssVars.neutral.text }}>
              {t('categories')}
            </span>
            {expandedSections.categories ? (
              <ChevronUp className="h-4 w-4" style={{ color: cssVars.neutral.textSecondary }} />
            ) : (
              <ChevronDown className="h-4 w-4" style={{ color: cssVars.neutral.textSecondary }} />
            )}
          </button>
          {expandedSections.categories && (
            <div className="space-y-2">
              {categories.map((category) => (
                <label key={category.id} className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.id)}
                    onChange={() => handleCategoryToggle(category.id)}
                    className="h-4 w-4 rounded"
                    style={{ accentColor: cssVars.primary.DEFAULT }}
                  />
                  <span className="text-sm" style={{ color: cssVars.neutral.text }}>
                    {isMockData ? (
                      <MockDataMarker>{category.label}</MockDataMarker>
                    ) : (
                      category.label
                    )}
                  </span>
                  {category.count !== undefined && (
                    <span
                      className="ms-auto text-xs"
                      style={{ color: cssVars.neutral.textSecondary }}
                    >
                      ({category.count})
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Price Range */}
      <div className="mb-6">
        <button
          onClick={() => toggleSection('price')}
          className="mb-3 flex w-full items-center justify-between"
        >
          <span className="font-semibold" style={{ color: cssVars.neutral.text }}>
            {t('priceRange')}
          </span>
          {expandedSections.price ? (
            <ChevronUp className="h-4 w-4" style={{ color: cssVars.neutral.textSecondary }} />
          ) : (
            <ChevronDown className="h-4 w-4" style={{ color: cssVars.neutral.textSecondary }} />
          )}
        </button>
        {expandedSections.price && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={priceMin}
                onChange={(e) => setPriceMin(Number(e.target.value))}
                onBlur={handlePriceChange}
                placeholder={t('min')}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{
                  borderColor: cssVars.neutral.border,
                  backgroundColor: cssVars.neutral.bg,
                }}
              />
              <span style={{ color: cssVars.neutral.textSecondary }}>-</span>
              <input
                type="number"
                value={priceMax}
                onChange={(e) => setPriceMax(Number(e.target.value))}
                onBlur={handlePriceChange}
                placeholder={t('max')}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{
                  borderColor: cssVars.neutral.border,
                  backgroundColor: cssVars.neutral.bg,
                }}
              />
            </div>
            <div className="text-xs" style={{ color: cssVars.neutral.textSecondary }}>
              {t('currency')}
            </div>
          </div>
        )}
      </div>

      {/* Delivery Time */}
      <div className="mb-6">
        <button
          onClick={() => toggleSection('delivery')}
          className="mb-3 flex w-full items-center justify-between"
        >
          <span className="font-semibold" style={{ color: cssVars.neutral.text }}>
            {t('deliveryTime')}
          </span>
          {expandedSections.delivery ? (
            <ChevronUp className="h-4 w-4" style={{ color: cssVars.neutral.textSecondary }} />
          ) : (
            <ChevronDown className="h-4 w-4" style={{ color: cssVars.neutral.textSecondary }} />
          )}
        </button>
        {expandedSections.delivery && (
          <div className="space-y-2">
            {[3, 7, 14, 30].map((days) => (
              <label key={days} className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="deliveryTime"
                  checked={deliveryTime === days}
                  onChange={() => handleDeliveryTimeChange(days)}
                  className="h-4 w-4"
                  style={{ accentColor: cssVars.primary.DEFAULT }}
                />
                <span className="text-sm" style={{ color: cssVars.neutral.text }}>
                  {t('upToDays', { days })}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Rating */}
      <div>
        <button
          onClick={() => toggleSection('rating')}
          className="mb-3 flex w-full items-center justify-between"
        >
          <span className="font-semibold" style={{ color: cssVars.neutral.text }}>
            {t('rating')}
          </span>
          {expandedSections.rating ? (
            <ChevronUp className="h-4 w-4" style={{ color: cssVars.neutral.textSecondary }} />
          ) : (
            <ChevronDown className="h-4 w-4" style={{ color: cssVars.neutral.textSecondary }} />
          )}
        </button>
        {expandedSections.rating && (
          <div className="space-y-2">
            {[4, 3, 2, 1, 0].map((rating) => (
              <label key={rating} className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="rating"
                  checked={minRating === rating}
                  onChange={() => handleRatingChange(rating)}
                  className="h-4 w-4"
                  style={{ accentColor: cssVars.primary.DEFAULT }}
                />
                <span
                  className="flex items-center gap-1 text-sm"
                  style={{ color: cssVars.neutral.text }}
                >
                  {rating > 0 ? (
                    <>
                      {rating}+ <span style={{ color: cssVars.accent.warm }}>★</span>
                    </>
                  ) : (
                    t('all')
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
